import { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ChevronDown, ChevronRight, Gauge, Check, Info, Settings2, Fuel, Car, Palette, Zap, Cog, Camera, Search, X, Phone, Sparkles } from 'lucide-react';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import SeoCityCluster from '../components/SeoCityCluster';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { trackPageView, trackCallClick } from '../utils/analytics';
import { useTranslation } from 'react-i18next';
import { SEO, generateInventoryListSchema } from '../components/SEO';
import { LiveViewers } from '../components/LiveViewers';
import { CarfaxReport, hasCarfaxReport, isCarfaxAccidentFree, isCarfaxOneOwner } from '../components/CarfaxReport';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const DEALERSHIP_PHONE = '(833) 995-6477';

// Debounce hook for search
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

// Helper to normalize image URL - handles relative paths
const normalizeImageUrl = (url) => {
  if (!url) return null;
  // Convert relative paths to full URLs
  if (url.startsWith('/api/')) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

// Helper to get thumbnail URL for fast grid loading
const getThumbnailUrl = (url) => {
  if (!url) return null;
  
  // First normalize the URL
  const normalizedUrl = normalizeImageUrl(url);
  
  // Skip thumbnail processing for placeholder images - use full quality
  if (normalizedUrl.includes('customer-assets.emergentagent.com') || 
      normalizedUrl.includes('static.prod-images.emergentagent.com') ||
      normalizedUrl.includes('entrepot/placeholders')) {
    return normalizedUrl;
  }
  
  if (normalizedUrl.includes('image.autousagee.ca')) {
    return `${API}/proxy/thumbnail?url=${encodeURIComponent(normalizedUrl)}`;
  }
  
  // Handle local uploads - check for /api/uploads/ pattern
  if (url.includes('/api/uploads/') && !url.includes('/thumbnails/')) {
    const filename = url.split('/').pop();
    const thumbName = filename.replace(/\.(webp|jpg|jpeg|png)$/i, '_thumb.webp');
    return `${BACKEND_URL}/api/uploads/thumbnails/${thumbName}`;
  }
  
  return normalizedUrl;
};

// Body type labels in French
const bodyTypeLabels = {
  sedan: 'Berline',
  suv: 'VUS',
  truck: 'Camion',
  coupe: 'Coupé',
  hatchback: 'Hayon',
  van: 'Fourgon',
  convertible: 'Cabriolet',
};

// Car brand logos - using CDN for HD quality
const brandLogos = {
  'Acura': 'https://www.carlogos.org/car-logos/acura-logo.png',
  'Alfa Romeo': 'https://www.carlogos.org/car-logos/alfa-romeo-logo.png',
  'Aston Martin': 'https://www.carlogos.org/car-logos/aston-martin-logo.png',
  'Audi': 'https://www.carlogos.org/car-logos/audi-logo.png',
  'BMW': 'https://www.carlogos.org/car-logos/bmw-logo.png',
  'Bentley': 'https://www.carlogos.org/car-logos/bentley-logo.png',
  'Buick': 'https://www.carlogos.org/car-logos/buick-logo.png',
  'Cadillac': 'https://www.carlogos.org/car-logos/cadillac-logo.png',
  'Chevrolet': 'https://www.carlogos.org/car-logos/chevrolet-logo.png',
  'Chrysler': 'https://www.carlogos.org/car-logos/chrysler-logo.png',
  'Dodge': 'https://www.carlogos.org/car-logos/dodge-logo.png',
  'Ferrari': 'https://www.carlogos.org/car-logos/ferrari-logo.png',
  'Fiat': 'https://www.carlogos.org/car-logos/fiat-logo.png',
  'Ford': 'https://www.carlogos.org/car-logos/ford-logo.png',
  'GMC': 'https://www.carlogos.org/car-logos/gmc-logo.png',
  'Genesis': 'https://www.carlogos.org/car-logos/genesis-logo.png',
  'Honda': 'https://www.carlogos.org/car-logos/honda-logo.png',
  'Hyundai': 'https://www.carlogos.org/car-logos/hyundai-logo.png',
  'Infiniti': 'https://www.carlogos.org/car-logos/infiniti-logo.png',
  'INFINITI': 'https://www.carlogos.org/car-logos/infiniti-logo.png',
  'Jaguar': 'https://www.carlogos.org/car-logos/jaguar-logo.png',
  'Jeep': 'https://www.carlogos.org/car-logos/jeep-logo.png',
  'Kia': 'https://www.carlogos.org/car-logos/kia-logo.png',
  'Lamborghini': 'https://www.carlogos.org/car-logos/lamborghini-logo.png',
  'Land Rover': 'https://www.carlogos.org/car-logos/land-rover-logo.png',
  'Lexus': 'https://www.carlogos.org/car-logos/lexus-logo.png',
  'Lincoln': 'https://www.carlogos.org/car-logos/lincoln-logo.png',
  'Maserati': 'https://www.carlogos.org/car-logos/maserati-logo.png',
  'Mazda': 'https://www.carlogos.org/car-logos/mazda-logo.png',
  'Mercedes-Benz': 'https://www.carlogos.org/car-logos/mercedes-benz-logo.png',
  'Mercedes': 'https://www.carlogos.org/car-logos/mercedes-benz-logo.png',
  'MB': 'https://www.carlogos.org/car-logos/mercedes-benz-logo.png',
  'Mini': 'https://www.carlogos.org/car-logos/mini-logo.png',
  'Mitsubishi': 'https://www.carlogos.org/car-logos/mitsubishi-logo.png',
  'Mustang': 'https://www.carlogos.org/car-logos/ford-logo.png',
  'Nissan': 'https://www.carlogos.org/car-logos/nissan-logo.png',
  'Nissan ': 'https://www.carlogos.org/car-logos/nissan-logo.png',
  'Porsche': 'https://www.carlogos.org/car-logos/porsche-logo.png',
  'Ram': 'https://www.carlogos.org/car-logos/ram-logo.png',
  'Subaru': 'https://www.carlogos.org/car-logos/subaru-logo.png',
  'Tesla': 'https://www.carlogos.org/car-logos/tesla-logo.png',
  'Toyota': 'https://www.carlogos.org/car-logos/toyota-logo.png',
  'Volkswagen': 'https://www.carlogos.org/car-logos/volkswagen-logo.png',
  'VW': 'https://www.carlogos.org/car-logos/volkswagen-logo.png',
  'Volvo': 'https://www.carlogos.org/car-logos/volvo-logo.png',
};

// Collapsible Filter Section Component
const FilterSection = ({ title, icon, children, defaultOpen = true }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="border-b border-gray-200 py-4">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full text-left group"
      >
        <div className="flex items-center gap-2">
          {icon && <span className="text-gray-500">{icon}</span>}
          <span className="font-semibold text-gray-800">{title}</span>
        </div>
        <div className={`w-6 h-6 rounded-full bg-gray-100 group-hover:bg-gray-200 flex items-center justify-center transition-all ${isOpen ? 'rotate-180' : ''}`}>
          <ChevronDown className="h-4 w-4 text-gray-600" />
        </div>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-[500px] opacity-100 mt-4' : 'max-h-0 opacity-0'}`}>
        {children}
      </div>
    </div>
  );
};

// Checkbox Item Component
const CheckboxItem = ({ label, count, checked, onChange, logo }) => (
  <label className="flex items-center gap-3 py-2.5 cursor-pointer hover:bg-gray-50 px-3 rounded-lg transition-colors group">
    <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all flex-shrink-0 ${
      checked ? 'bg-[#3b82f6] border-[#3b82f6] scale-110' : 'border-gray-300 group-hover:border-gray-400'
    }`}>
      {checked && <Check className="h-3 w-3 text-white" />}
    </div>
    {logo && (
      <img 
        src={logo} 
        alt="" 
        className="h-6 w-6 object-contain flex-shrink-0 bg-gray-100 rounded p-0.5" 
        onError={(e) => { e.target.style.display = 'none'; }}
      />
    )}
    <span className={`flex-1 transition-colors ${checked ? 'text-gray-900 font-medium' : 'text-gray-600'}`}>{label}</span>
    {count !== undefined && (
      <span className={`text-sm px-2 py-0.5 rounded-full transition-colors ${checked ? 'bg-[#3b82f6] text-white' : 'bg-gray-100 text-gray-600'}`}>
        {count}
      </span>
    )}
    <input type="checkbox" checked={checked} onChange={onChange} className="sr-only" />
  </label>
);

// Scrollable list of makes with a fade-out gradient + animated chevron-down hint
// when there is more content below the visible area.
const ScrollableMakesList = ({ makes, selectedMakes, onToggle, brandLogos }) => {
  const scrollRef = useRef(null);
  const [showHint, setShowHint] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => {
      const hasOverflow = el.scrollHeight > el.clientHeight + 4;
      const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 16;
      setShowHint(hasOverflow && !nearBottom);
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, [makes.length]);

  const scrollMore = () => {
    scrollRef.current?.scrollBy({ top: 160, behavior: 'smooth' });
  };

  return (
    <div className="relative">
      <div
        ref={scrollRef}
        className="max-h-96 overflow-y-auto space-y-0.5 pr-1 custom-scrollbar"
        data-testid="makes-scroll-area"
      >
        {makes.map((make) => (
          <CheckboxItem
            key={make.name}
            label={make.name}
            count={make.count}
            checked={selectedMakes.includes(make.name)}
            onChange={() => onToggle(make.name)}
            logo={brandLogos[make.name]}
          />
        ))}
      </div>
      {/* Bottom fade gradient */}
      <div
        className={`pointer-events-none absolute left-0 right-1 bottom-0 h-12 bg-gradient-to-t from-white via-white/80 to-transparent transition-opacity ${
          showHint ? 'opacity-100' : 'opacity-0'
        }`}
      />
      {/* Pulsing chevron hint button — clickable to scroll more */}
      <button
        type="button"
        onClick={scrollMore}
        aria-label="Scroll for more brands"
        data-testid="makes-scroll-hint"
        className={`absolute left-1/2 -translate-x-1/2 bottom-1 w-7 h-7 rounded-full bg-[#3b82f6] text-white flex items-center justify-center shadow-md transition-all hover:bg-blue-600 hover:scale-110 ${
          showHint ? 'opacity-100 animate-bounce' : 'opacity-0 pointer-events-none'
        }`}
      >
        <ChevronDown className="h-4 w-4" />
      </button>
    </div>
  );
};

// Vehicle Card Component - Redesigned to match reference layout
const VehicleCard = ({ vehicle, priority = false }) => {
  const { t, i18n } = useTranslation();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageHovered, setImageHovered] = useState(false);
  // High quality placeholder image
  const defaultImage = 'https://res.cloudinary.com/dtqyyb6su/image/upload/v1774460688/entrepot/placeholders/new_arrival_placeholder.webp';
  const photoCount = vehicle.images?.length || 0;

  const formatPrice = (price) => {
    if (!price || price === 0) return null;
    return new Intl.NumberFormat(i18n.language === 'en' ? 'en-CA' : i18n.language === 'es' ? 'es' : i18n.language === 'ar' ? 'ar' : 'fr-CA', {
      style: 'currency', currency: 'CAD', minimumFractionDigits: 0,
    }).format(price);
  };

  const formatMileage = (mileage) => new Intl.NumberFormat(i18n.language === 'ar' ? 'ar' : i18n.language === 'es' ? 'es' : i18n.language === 'en' ? 'en-CA' : 'fr-CA').format(mileage || 0);
  const getTransmissionLabel = (trans) => {
    if (!trans) return t('vehicle.automatic');
    const val = trans.toLowerCase();
    if (val === 'automatic' || val === 'auto' || val === 'automatique') return t('vehicle.automatic');
    if (val === 'cvt') return t('vehicle.cvt');
    if (val === 'manual' || val === 'manuelle' || val === 'manuel') return t('vehicle.manual');
    return trans;
  };
  const getDrivetrainLabel = (d) => {
    if (!d) return '-';
    const val = d.toLowerCase();
    if (val === 'awd') return t('vehicle.awd');
    if (val === 'fwd') return t('vehicle.fwd');
    if (val === 'rwd') return t('vehicle.rwd');
    if (val === '4wd' || val === '4x4') return t('vehicle.4wd');
    return d.toUpperCase();
  };

  // Calculate rebate if msrp and price both exist
  const hasRebate = vehicle.msrp && vehicle.price && vehicle.msrp > vehicle.price;
  const rebateAmount = hasRebate ? vehicle.msrp - vehicle.price : 0;

  return (
    <div className="bg-white rounded-xl overflow-hidden border border-slate-200 shadow-sm hover:shadow-xl hover:shadow-blue-500/10 transition-all duration-300 flex flex-col" data-testid={`vehicle-card-${vehicle.id}`}>
      {/* Image Section */}
      <Link to={`/vehicle/${vehicle.id}`} className="block relative overflow-hidden bg-slate-100 group" style={{ aspectRatio: '3/2' }}
        onMouseEnter={() => setImageHovered(true)}
        onMouseLeave={() => setImageHovered(false)}
      >
        {/* Live Viewers Badge */}
        <LiveViewers vehicleId={vehicle.id} variant="card" />
        
        {!imageLoaded && (
          <div className="absolute inset-0 bg-gradient-to-br from-zinc-800 to-zinc-700 animate-pulse" />
        )}
        {/* Video autoplay - plays when visible on screen */}
        {vehicle.video_url && (
          <video
            ref={(el) => {
              if (!el) return;
              const observer = new IntersectionObserver(
                ([entry]) => {
                  if (entry.isIntersecting) {
                    el.play().catch(() => {});
                  } else {
                    el.pause();
                  }
                },
                { threshold: 0.3 }
              );
              observer.observe(el);
              el._observer = observer;
            }}
            src={vehicle.video_url.includes('cloudinary.com') 
              ? vehicle.video_url.replace('/upload/', '/upload/w_360,q_auto:eco,br_500k,f_mp4/') 
              : vehicle.video_url}
            muted
            loop
            playsInline
            preload="metadata"
            className="absolute inset-0 w-full h-full object-cover z-10"
          />
        )}
        
        {/* Photo for vehicles without video */}
        {!vehicle.video_url && (
          <img
            src={getThumbnailUrl(vehicle.images?.[0]) || defaultImage}
            alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
            width={800}
            height={533}
            loading={priority ? 'eager' : 'lazy'}
            decoding="async"
            fetchpriority={priority ? 'high' : 'auto'}
            onLoad={() => setImageLoaded(true)}
            onError={(e) => { e.target.src = defaultImage; setImageLoaded(true); }}
            className={`absolute inset-0 w-full h-full object-cover transition-transform duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'} ${imageHovered ? 'scale-105' : ''}`}
          />
        )}
        
        {/* Video badge */}
        {vehicle.video_url && (
          <div className="absolute top-2 left-2 bg-black/70 text-white px-2 py-0.5 rounded-full flex items-center gap-1 text-[10px] font-medium z-20">
            <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
          </div>
        )}
        
        {/* Photo count on hover */}
        {photoCount > 0 && (
          <div className="absolute bottom-2 left-2 bg-black/70 text-white px-2 py-1 rounded-lg flex items-center gap-1.5 text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity">
            <Camera className="h-3.5 w-3.5" />
            <span>{photoCount}</span>
          </div>
        )}
        
        {/* Status badges */}
        {vehicle.status === 'sold' && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
            <span className="font-bold text-2xl text-white">VENDU</span>
          </div>
        )}
        {vehicle.status === 'reserved' && (
          <span className="absolute top-2 left-2 bg-purple-600 text-white text-xs px-3 py-1 rounded-full font-bold">
            RÉSERVÉ
          </span>
        )}
      </Link>

      {/* Vehicle Title */}
      <div className="px-4 pt-3 pb-1">
        <h3 className="font-bold text-slate-900 text-sm sm:text-base leading-tight">
          {vehicle.year} {vehicle.make} {vehicle.model}
        </h3>
        <p className="text-slate-400 text-[10px] mt-0.5 uppercase leading-tight line-clamp-2">
          {vehicle.trim}{vehicle.title ? ` | ${vehicle.title}` : ''}
        </p>
      </div>

      {/* Three Icons Row: Drivetrain | Transmission | Mileage */}
      <div className="px-4 py-2 border-t border-b border-slate-100">
        <div className="grid grid-cols-3 gap-2 text-center">
          {/* Drivetrain - AWD/FWD diagram */}
          <div className="flex flex-col items-center">
            <svg className="h-4 w-4 text-slate-400 mb-0.5" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.5">
              {/* Top axle with wheels */}
              <circle cx="8" cy="8" r="3" />
              <circle cx="24" cy="8" r="3" />
              <line x1="11" y1="8" x2="21" y2="8" />
              {/* Center shaft */}
              <line x1="16" y1="8" x2="16" y2="24" />
              {/* Bottom axle with wheels */}
              <circle cx="8" cy="24" r="3" />
              <circle cx="24" cy="24" r="3" />
              <line x1="11" y1="24" x2="21" y2="24" />
            </svg>
            <span className="text-[10px] text-slate-500">{getDrivetrainLabel(vehicle.drivetrain)}</span>
          </div>
          
          {/* Transmission - Gear shift with boot */}
          <div className="flex flex-col items-center">
            <svg className="h-4 w-4 text-slate-400 mb-0.5" viewBox="0 0 32 32" fill="currentColor">
              {/* Knob */}
              <circle cx="10" cy="6" r="5" />
              {/* Shaft */}
              <rect x="8" y="9" width="4" height="10" rx="1" transform="rotate(-20 10 14)" />
              {/* Boot stripes */}
              <rect x="4" y="20" width="24" height="3" rx="1.5" />
              <rect x="6" y="24" width="20" height="3" rx="1.5" />
              <rect x="8" y="28" width="16" height="3" rx="1.5" />
            </svg>
            <span className="text-[10px] text-slate-500">{getTransmissionLabel(vehicle.transmission)}</span>
          </div>
          
          {/* Mileage - Speedometer */}
          <div className="flex flex-col items-center">
            <svg className="h-4 w-4 text-slate-400 mb-0.5" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.5">
              {/* Outer circle (partial) */}
              <path d="M6 22 A12 12 0 1 1 26 22" />
              {/* Speed marks */}
              <line x1="16" y1="6" x2="16" y2="9" />
              <line x1="8" y1="10" x2="10" y2="12" />
              <line x1="24" y1="10" x2="22" y2="12" />
              <line x1="5" y1="18" x2="8" y2="18" />
              <line x1="27" y1="18" x2="24" y2="18" />
              {/* Needle */}
              <line x1="16" y1="18" x2="22" y2="12" strokeWidth="2" />
              {/* Center dot */}
              <circle cx="16" cy="18" r="2" fill="currentColor" />
            </svg>
            <span className="text-[10px] text-slate-500">{formatMileage(vehicle.mileage)} km</span>
          </div>
        </div>
      </div>

      {/* Price Section */}
      <div className="px-4 py-3 flex-1">
        {/* Dealer Rebate (if applicable) */}
        {hasRebate && (
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-slate-600">
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" />
                <circle cx="7" cy="7" r="1" fill="currentColor" />
              </svg>
              <span className="text-sm">{t('vehicle.dealer_rebate')}</span>
            </div>
            <span className="text-sm font-semibold text-slate-800">{formatPrice(rebateAmount)}</span>
          </div>
        )}

        {/* Selling Price */}
        <div className="flex items-center justify-between">
          <span className="font-bold text-slate-800">{t('vehicle.selling_price')}</span>
          <div className="text-right">
            {vehicle.regular_price && vehicle.regular_price > vehicle.price ? (
              <div className="bg-[#0c2340]/5 border border-[#0c2340]/20 rounded-lg p-2 min-w-[100px] text-center">
                <span className="inline-block bg-[#0c2340] text-white text-[8px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider mb-1">Promo</span>
                <p className="text-[9px] text-slate-400 line-through leading-tight">{formatPrice(vehicle.regular_price)}</p>
                <p className="font-black text-base text-[#0c2340] leading-tight">{formatPrice(vehicle.price)}</p>
                <p className="text-[8px] text-emerald-600 font-semibold mt-0.5">
                  Économisez {new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 0 }).format(vehicle.regular_price - vehicle.price)}
                </p>
              </div>
            ) : (
              <>
                {hasRebate && (
                  <span className="text-sm text-slate-400 line-through block">{formatPrice(vehicle.msrp)}</span>
                )}
                {vehicle.price && vehicle.price > 0 ? (
                  <span className="text-xl font-black text-[#3b82f6]">*{formatPrice(vehicle.price)}</span>
                ) : (
                  <span className="text-sm font-semibold text-slate-600">{t('vehicle.call_us')}</span>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* See Details Button */}
      <div className="px-4 pb-4">
        <Link
          to={`/vehicle/${vehicle.id}`}
          className="block w-full bg-slate-800 hover:bg-slate-900 text-white text-center py-3 rounded font-semibold text-sm uppercase tracking-wide transition-colors"
          data-testid={`vehicle-details-btn-${vehicle.id}`}
        >
          {t('vehicle.view_details')}
        </Link>
      </div>

      {/* CARFAX Section */}
      {(hasCarfaxReport(vehicle) || isCarfaxOneOwner(vehicle) || isCarfaxAccidentFree(vehicle)) && (
        <div className="px-4 pb-4 pt-3 border-t border-slate-100">
          <CarfaxReport vehicle={vehicle} height={30} />
        </div>
      )}
    </div>
  );
};

// Large Promotional Card Component - Responsive: 1 col on mobile, 2 cols on desktop
const LargePromoCard = ({ type, title, subtitle, image, buttonText, buttonLink }) => {
  // If there's an image, display it as a full clickable banner
  if (image) {
    // Use object-contain to show full image without cropping
    // Background matches typical promo colors
    const imageContent = (
      <div className="relative w-full h-full min-h-[280px] sm:min-h-[320px] lg:min-h-[380px] bg-gradient-to-br from-slate-800 to-slate-900">
        <img 
          src={image} 
          alt={title || 'Promotion'} 
          className="absolute inset-0 w-full h-full object-contain"
        />
      </div>
    );
    
    if (buttonLink) {
      return (
        <Link to={buttonLink} className="col-span-1 sm:col-span-2 block rounded-xl overflow-hidden group h-full">
          {imageContent}
        </Link>
      );
    }
    return (
      <div className="col-span-1 sm:col-span-2 rounded-xl overflow-hidden group h-full">
        {imageContent}
      </div>
    );
  }

  // Fallback for text-based promos (legacy support)
  const backgrounds = {
    financing: 'bg-gradient-to-br from-green-600 to-green-700',
    credit: 'bg-gradient-to-br from-blue-600 to-blue-700',
    warranty: 'bg-gradient-to-br from-purple-600 to-purple-700',
    trade: 'bg-gradient-to-br from-orange-500 to-red-600',
    service: 'bg-gradient-to-br from-slate-700 to-slate-800',
    default: 'bg-gradient-to-br from-blue-600 to-blue-700',
  };

  return (
    <div className={`col-span-1 sm:col-span-2 ${backgrounds[type] || backgrounds.default} rounded-xl overflow-hidden text-white p-6 sm:p-8 flex flex-col justify-center items-center text-center`}>
      <h3 className="font-black text-2xl sm:text-3xl md:text-4xl mb-3 uppercase tracking-wide leading-tight">{title}</h3>
      <p className="text-white/80 text-base sm:text-lg mb-6 max-w-md">{subtitle}</p>
      {buttonText && (
        <Link 
          to={buttonLink || '/contact'}
          className="bg-white text-slate-800 font-bold px-8 py-3 rounded-lg hover:bg-slate-100 transition-colors text-sm uppercase tracking-wide"
        >
          {buttonText}
        </Link>
      )}
    </div>
  );
};

// Default promotional content for large cards
const defaultLargePromos = [
  {
    type: 'financing',
    title: 'Financement Flexible',
    subtitle: 'Taux compétitifs à partir de 4.99% - Approbation rapide en moins de 24 heures!',
    buttonText: 'Pré-qualification Gratuite',
    buttonLink: '/credit',
  },
  {
    type: 'trade',
    title: 'Échange Accepté',
    subtitle: 'Obtenez le meilleur prix garanti pour votre véhicule actuel. Évaluation gratuite et sans obligation!',
    buttonText: 'Évaluation Gratuite',
    buttonLink: '/vendre',
  },
  {
    type: 'credit',
    title: 'Crédit Approuvé',
    subtitle: 'Peu importe votre situation de crédit, nous avons une solution de financement pour vous!',
    buttonText: 'Appliquer Maintenant',
    buttonLink: '/credit',
  },
  {
    type: 'warranty',
    title: 'Garantie Prolongée',
    subtitle: 'Roulez en toute tranquillité avec notre protection complète disponible jusqu\'à 5 ans.',
    buttonText: 'En Savoir Plus',
    buttonLink: '/contact',
  },
];

const InventoryPage = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [searchParams, setSearchParams] = useSearchParams();
  const [vehicles, setVehicles] = useState([]);
  const [allVehicles, setAllVehicles] = useState([]); // Cache for client-side filtering
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(false);
  const [makeStats, setMakeStats] = useState([]);
  const [bodyTypeStats, setBodyTypeStats] = useState([]);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const debouncedSearch = useDebounce(searchQuery, 300); // 300ms debounce
  const [filters, setFilters] = useState({
    makes: searchParams.get('make') ? [searchParams.get('make')] : [],
    models: [],
    body_types: [],
    price_min: searchParams.get('price_min') ? parseInt(searchParams.get('price_min')) : null,
    price_max: searchParams.get('price_max') ? parseInt(searchParams.get('price_max')) : null,
    mileage_max: null,
  });
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showAllMakes, setShowAllMakes] = useState(false);
  const [promoImages, setPromoImages] = useState([]);

  // Fetch vehicles only when filters change (not on every search keystroke)
  useEffect(() => {
    fetchVehicles();
  }, [filters, sortBy, sortOrder]);

  // Load all vehicles once on mount for fast client-side search
  useEffect(() => {
    // Track inventory page view
    trackPageView('inventory');
    
    fetchAllVehicles();
    fetchStats();
    fetchPromoImages();
  }, []);

  const fetchAllVehicles = async () => {
    try {
      setApiError(false);
      const response = await axios.get(`${API}/vehicles?limit=1000`);
      const available = response.data.filter(v => ['available', 'new_arrival'].includes(v.status));
      setAllVehicles(available);
    } catch (error) {
      console.error('Error fetching all vehicles:', error);
      setApiError(true);
    }
  };

  // Client-side filtered vehicles based on search and filters
  const filteredVehicles = useMemo(() => {
    let result = allVehicles;
    
    // Apply make filter (case-insensitive — handles "KIA" vs "Kia" duplicates)
    if (filters.makes.length > 0) {
      const lowerSelected = filters.makes.map(m => (m || '').trim().toLowerCase());
      result = result.filter(v => lowerSelected.includes((v.make || '').trim().toLowerCase()));
    }
    // Apply model filter
    if (filters.models.length > 0) {
      result = result.filter(v => filters.models.includes(v.model));
    }
    // Apply body type filter
    if (filters.body_types.length > 0) {
      result = result.filter(v => filters.body_types.includes(v.body_type));
    }
    // Apply price filters
    if (filters.price_min) {
      result = result.filter(v => v.price >= filters.price_min);
    }
    if (filters.price_max) {
      result = result.filter(v => v.price <= filters.price_max);
    }
    // Apply mileage filter
    if (filters.mileage_max) {
      result = result.filter(v => v.mileage <= filters.mileage_max);
    }
    
    // Apply search query (debounced)
    if (debouncedSearch && debouncedSearch.trim()) {
      const query = debouncedSearch.toLowerCase().trim();
      result = result.filter(v => {
        const vin = (v.vin || '').toLowerCase();
        const stock = (v.stock_number || '').toLowerCase();
        return (
          v.make?.toLowerCase().includes(query) ||
          v.model?.toLowerCase().includes(query) ||
          v.year?.toString().includes(query) ||
          v.trim?.toLowerCase().includes(query) ||
          `${v.year} ${v.make} ${v.model}`.toLowerCase().includes(query) ||
          stock.includes(query) ||           // full or partial stock number
          vin.includes(query) ||              // full VIN match
          (query.length >= 4 && vin.endsWith(query))  // last N chars of VIN (4-17)
        );
      });
    }
    
    // Apply sorting
    result = [...result].sort((a, b) => {
      const aVal = a[sortBy] || 0;
      const bVal = b[sortBy] || 0;
      if (sortOrder === 'asc') return aVal > bVal ? 1 : -1;
      return aVal < bVal ? 1 : -1;
    });
    
    return result;
  }, [allVehicles, filters, debouncedSearch, sortBy, sortOrder]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/vehicles/stats`);
      // Create make stats with counts
      const rawMakes = response.data.makes || [];
      // Fetch all vehicles to count by make
      const vehiclesRes = await axios.get(`${API}/vehicles?limit=1000`);
      const allVehicles = vehiclesRes.data.filter(v => ['available', 'new_arrival'].includes(v.status));

      // Deduplicate makes case-insensitively (e.g. "KIA" and "Kia" → "Kia"),
      // picking the most common casing for the canonical name.
      const casingCounts = {};
      allVehicles.forEach(v => {
        if (!v.make) return;
        const key = v.make.trim().toLowerCase();
        if (!casingCounts[key]) casingCounts[key] = {};
        casingCounts[key][v.make] = (casingCounts[key][v.make] || 0) + 1;
      });
      const canonical = {};
      Object.entries(casingCounts).forEach(([key, casings]) => {
        canonical[key] = Object.entries(casings).sort((a, b) => b[1] - a[1])[0][0];
      });
      // Aggregate counts across casings
      const makeCounts = {};
      const bodyCounts = {};
      allVehicles.forEach(v => {
        if (v.make) {
          const name = canonical[v.make.trim().toLowerCase()] || v.make;
          makeCounts[name] = (makeCounts[name] || 0) + 1;
        }
        if (v.body_type) {
          bodyCounts[v.body_type] = (bodyCounts[v.body_type] || 0) + 1;
        }
      });

      // Unique deduplicated make list
      const seen = new Set();
      const dedupedMakes = [];
      rawMakes.forEach(m => {
        const key = (m || '').trim().toLowerCase();
        if (!key || seen.has(key)) return;
        seen.add(key);
        dedupedMakes.push(canonical[key] || m);
      });

      setMakeStats(dedupedMakes.map(make => ({ name: make, count: makeCounts[make] || 0 })).sort((a, b) => b.count - a.count));
      setBodyTypeStats(Object.entries(bodyCounts).map(([type, count]) => ({ name: type, count })).sort((a, b) => b.count - a.count));
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchPromoImages = async () => {
    try {
      const response = await axios.get(`${API}/promos`);
      setPromoImages(response.data || []);
    } catch (error) {
      console.error('Error fetching promos:', error);
      setPromoImages([]);
    }
  };

  const fetchVehicles = async () => {
    setLoading(true);
    try {
      setApiError(false);
      const response = await axios.get(`${API}/vehicles?limit=1000`);
      const available = response.data.filter(v => ['available', 'new_arrival'].includes(v.status));
      setAllVehicles(available);
      setVehicles(available);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      setApiError(true);
    } finally {
      setLoading(false);
    }
  };

  const toggleMakeFilter = (make) => {
    setFilters(prev => {
      const newMakes = prev.makes.includes(make) 
        ? prev.makes.filter(m => m !== make)
        : [...prev.makes, make];
      // Clear model selections that no longer match selected makes
      const validModels = prev.models.filter(model => 
        allVehicles.some(v => newMakes.includes(v.make) && v.model === model)
      );
      return { ...prev, makes: newMakes, models: validModels };
    });
  };

  const toggleModelFilter = (model) => {
    setFilters(prev => ({
      ...prev,
      models: prev.models.includes(model) 
        ? prev.models.filter(m => m !== model)
        : [...prev.models, model]
    }));
  };

  // Compute available models based on selected makes
  const modelStats = useMemo(() => {
    if (filters.makes.length === 0) return [];
    const modelCounts = {};
    allVehicles
      .filter(v => filters.makes.includes(v.make) && ['available', 'new_arrival'].includes(v.status))
      .forEach(v => {
        if (v.model) {
          modelCounts[v.model] = (modelCounts[v.model] || 0) + 1;
        }
      });
    return Object.entries(modelCounts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [allVehicles, filters.makes]);

  const toggleBodyTypeFilter = (type) => {
    setFilters(prev => ({
      ...prev,
      body_types: prev.body_types.includes(type)
        ? prev.body_types.filter(t => t !== type)
        : [...prev.body_types, type]
    }));
  };

  const handleSortChange = (value) => {
    const [field, order] = value.split('-');
    setSortBy(field);
    setSortOrder(order);
  };

  const displayedMakes = showAllMakes ? makeStats : makeStats.slice(0, 10);

  // Insert large promo cards at specific positions
  // Pattern for 4-column grid (promos span 2 columns):
  // Row 1: 4 vehicles (items 0-3)
  // Row 2: 2 vehicles + promo (items 4-5, then promo at 6)
  // Row 3: 4 vehicles (items 7-10)
  // Row 4: promo + 2 vehicles (promo at 11, items 12-13)
  // Row 5: 4 vehicles (items 14-17)
  // Row 6: 1 vehicle + promo + 1 vehicle (item 18, promo at 19, item 20)
  // Pattern repeats every 21 items
  const getGridItems = () => {
    const items = [];
    let vehicleIndex = 0;
    let promoIndex = 0;
    
    // Only use promos from database - no defaults
    const promos = promoImages.length > 0 
      ? promoImages.map(p => ({
          type: p.promo_type || 'default',
          title: p.title,
          subtitle: p.subtitle,
          buttonText: p.button_text,
          buttonLink: p.button_link,
          image: p.image_url,
        }))
      : [];
    
    // If no promos uploaded yet, just show a clean vehicle grid
    if (promos.length === 0) {
      return filteredVehicles.map(v => ({ type: 'vehicle', data: v }));
    }
    
    // Promo positions in the 21-item pattern
    const promoPositions = [6, 11, 19];
    const patternLength = 21;
    
    while (vehicleIndex < filteredVehicles.length || promoIndex < promos.length) {
      const positionInPattern = items.length % patternLength;
      
      // Check if this position should have a promo
      if (promoPositions.includes(positionInPattern) && promoIndex < promos.length) {
        const position = positionInPattern === 6 ? 'right' : positionInPattern === 11 ? 'left' : 'middle';
        items.push({ type: 'large-promo', data: promos[promoIndex], position });
        promoIndex++;
      } else if (vehicleIndex < filteredVehicles.length) {
        items.push({ type: 'vehicle', data: filteredVehicles[vehicleIndex] });
        vehicleIndex++;
      } else {
        // No more vehicles or promos to show
        break;
      }
    }
    
    return items;
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="inventory-page" dir={isRTL ? 'rtl' : 'ltr'}>
      <SEO 
        title="Inventaire - Véhicules d'occasion"
        description={`Découvrez notre inventaire de ${filteredVehicles.length} véhicules d'occasion de qualité. Voitures, VUS, camions - tous inspectés et garantis. Financement disponible.`}
        url="https://entrepotautomontreal.ca/inventory"
        keywords="inventaire voiture occasion, auto usagé montréal, SUV occasion, camion usagé, véhicule certifié québec"
        schema={vehicles.length > 0 ? generateInventoryListSchema(vehicles) : null}
      />
      {/* Car Photo Background - Fixed parallax effect */}
      <div 
        className="fixed inset-0 z-0"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1762195340046-415140d8b1b2?w=1920&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/50 to-black/60"></div>
      </div>
      
      {/* Hero Section with Navbar - Full height */}
      <div className="relative z-10 bg-slate-900/80 overflow-hidden">
        {/* Background Image - Extended to cover header */}
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/17632052/pexels-photo-17632052.jpeg?w=1920" 
            alt="" 
            className="w-full h-full object-cover opacity-50"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/70 to-slate-900/30" />
        </div>
        
        {/* Navbar - white background */}
        <div className="relative z-10">
          <Navbar />
        </div>
        
        {/* Hero Content - Centered vertically */}
        <div className="relative px-4 sm:px-8 md:px-12 lg:px-16 py-10 sm:py-16 md:py-24">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 w-full">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="h-1 w-12 bg-[#3b82f6] rounded-full"></div>
                <span className="text-[#3b82f6] font-semibold text-mobile-sm md:text-sm uppercase tracking-wider">{t('hero.ourInventory')}</span>
              </div>
              <h1 className="text-mobile-h1 sm:text-tablet-h1 md:text-[2.75rem] lg:text-[3.25rem] font-bold text-white mb-4 leading-tight">
                {t('hero.findYourIdealVehicle')} <span className="text-[#3b82f6]">{t('hero.idealVehicle')}</span>
              </h1>
              <p className="text-slate-300 text-mobile-body md:text-lg lg:text-xl max-w-xl mb-6 leading-relaxed">
                {t('hero.browseSelection')}
              </p>
              
              {/* CTA Buttons */}
              <div className="flex flex-wrap gap-3 sm:gap-4">
                <a 
                  href="#vehicle-grid" 
                  className="bg-[#3b82f6] hover:bg-[#2563eb] text-white font-semibold px-5 sm:px-8 py-3 sm:py-4 rounded-full transition-colors flex items-center gap-2 text-mobile-sm sm:text-base"
                >
                  {t('hero.browseVehicles')}
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </a>
                <a 
                  href="/credit" 
                  className="bg-white/10 hover:bg-white/20 text-white font-semibold px-5 sm:px-8 py-3 sm:py-4 rounded-full transition-colors border border-white/30 text-mobile-sm sm:text-base"
                >
                  {t('hero.preQualification')}
                </a>
                <Link
                  to="/notre-processus"
                  data-testid="inventory-process-cta"
                  className="group relative bg-gradient-to-r from-blue-500/20 via-blue-400/20 to-blue-500/20 hover:from-blue-500/30 hover:to-blue-500/30 text-white font-semibold px-5 sm:px-8 py-3 sm:py-4 rounded-full transition-all border border-blue-400/40 hover:border-blue-300 flex items-center gap-2 text-mobile-sm sm:text-base shadow-lg shadow-blue-500/10 hover:shadow-blue-500/30 hover:scale-[1.02]"
                >
                  <Sparkles className="w-4 h-4 text-blue-300 group-hover:text-white transition-colors" />
                  {t('notreProcessus.inventoryCta')}
                  <span className="opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all">→</span>
                </Link>
              </div>
            </div>
            
            {/* Stats */}
            <div className="flex gap-10 md:gap-14">
              <div className="text-center">
                <p className="text-4xl md:text-5xl font-bold text-white">47+</p>
                <p className="text-slate-400 text-base">{t('hero.vehicles')}</p>
              </div>
              <div className="text-center">
                <p className="text-4xl md:text-5xl font-bold text-[#3b82f6]">100%</p>
                <p className="text-slate-400 text-base">{t('hero.inspected')}</p>
              </div>
              <div className="text-center">
                <p className="text-4xl md:text-5xl font-bold text-white">5★</p>
                <p className="text-slate-400 text-base">{t('hero.service')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - Full Width */}
      <div className="relative z-10 flex-1 px-8 md:px-12 lg:px-16 py-10 w-full">
        <div className="flex gap-10">
          {/* Left Sidebar Filters */}
          <aside className="hidden lg:block w-96 flex-shrink-0">
            <div className="bg-white rounded-2xl sticky top-20 shadow-xl border border-gray-200 overflow-hidden">
              {/* Search Box - Hero Style */}
              <div className="bg-gradient-to-r from-[#3b82f6] to-[#2563eb] p-6">
                <div className="flex items-center gap-2 mb-3">
                  <Search className="h-5 w-5 text-white/90" />
                  <h3 className="text-white font-semibold">{t('inventory.quickSearch')}</h3>
                </div>
                <div className="relative">
                  <input
                    type="text"
                    placeholder={t('inventory.searchPlaceholder')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full px-4 py-3.5 bg-white/95 backdrop-blur rounded-xl text-sm text-slate-800 placeholder-slate-400 focus:ring-2 focus:ring-white/50 outline-none transition-all shadow-lg"
                    data-testid="inventory-search-input"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 h-6 w-6 bg-slate-100 hover:bg-slate-200 rounded-full flex items-center justify-center transition-colors"
                    >
                      <X className="h-4 w-4 text-slate-500" />
                    </button>
                  )}
                </div>
                {searchQuery && (
                  <p className="text-blue-100 text-xs mt-2">
                    {filteredVehicles.length} {t('inventory.resultsFor')} "{searchQuery}"
                  </p>
                )}
              </div>
              
              {/* Filters Section */}
              <div className="p-6">
                {/* Sidebar Header */}
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                      <Settings2 className="h-4 w-4 text-gray-600" />
                    </div>
                    <h2 className="text-lg font-bold text-gray-800">{t('inventory.filters')}</h2>
                  </div>
                  {(filters.makes.length > 0 || filters.models.length > 0 || filters.body_types.length > 0 || filters.price_max || filters.mileage_max || searchQuery) && (
                    <button 
                      onClick={() => {
                        setFilters({ makes: [], models: [], body_types: [], price_min: null, price_max: null, mileage_max: null });
                        setSearchQuery('');
                      }}
                      className="text-[#3b82f6] text-sm font-medium hover:text-blue-700 flex items-center gap-1.5 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-full transition-colors"
                    >
                      <X className="w-3.5 h-3.5" />
                      {t('inventory.clearAll')}
                    </button>
                  )}
                </div>

                {/* Active Filters Pills */}
                {(filters.makes.length > 0 || filters.models.length > 0 || filters.body_types.length > 0) && (
                  <div className="flex flex-wrap gap-2 mb-5 pb-5 border-b border-gray-200">
                    {filters.makes.map(make => (
                      <span key={make} className="inline-flex items-center gap-1.5 bg-[#3b82f6] text-white text-xs font-medium px-3 py-1.5 rounded-full">
                        {make}
                        <button onClick={() => toggleMakeFilter(make)} className="hover:bg-blue-600 rounded-full p-0.5">
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                    {filters.models.map(model => (
                      <span key={model} className="inline-flex items-center gap-1.5 bg-[#2563eb] text-white text-xs font-medium px-3 py-1.5 rounded-full">
                        {model}
                        <button onClick={() => toggleModelFilter(model)} className="hover:bg-blue-700 rounded-full p-0.5">
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                    {filters.body_types.map(type => (
                      <span key={type} className="inline-flex items-center gap-1.5 bg-[#3b82f6] text-white text-xs font-medium px-3 py-1.5 rounded-full">
                        {bodyTypeLabels[type] || type}
                        <button onClick={() => toggleBodyTypeFilter(type)} className="hover:bg-blue-600 rounded-full p-0.5">
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              
                {/* Make & Model Filter */}
                <FilterSection 
                  title={t('inventory.brand')} 
                  icon={<Car className="h-4 w-4" />}
                  defaultOpen={true}
                >
                  <ScrollableMakesList
                    makes={displayedMakes}
                    selectedMakes={filters.makes}
                    onToggle={toggleMakeFilter}
                    brandLogos={brandLogos}
                  />
                  {makeStats.length > 10 && (
                    <button 
                      onClick={() => setShowAllMakes(!showAllMakes)}
                      className="text-[#3b82f6] text-sm font-medium mt-3 flex items-center gap-2 hover:gap-3 transition-all"
                    >
                      {showAllMakes ? `← ${t('inventory.showLess')}` : `${t('inventory.viewAllBrands')} →`}
                      <span className="bg-blue-50 text-[#3b82f6] text-xs px-2 py-0.5 rounded-full font-semibold">{makeStats.length}</span>
                    </button>
                  )}
                </FilterSection>

                {/* Model Filter - Only shows when a make is selected */}
                {filters.makes.length > 0 && modelStats.length > 0 && (
                  <FilterSection 
                    title={t('inventory.model') || 'Modèle'}
                    icon={<Car className="h-4 w-4" />}
                    defaultOpen={true}
                  >
                    <div className="max-h-60 overflow-y-auto space-y-0.5 pr-1 custom-scrollbar">
                      {modelStats.map((model) => (
                        <CheckboxItem
                          key={model.name}
                          label={model.name}
                          count={model.count}
                          checked={filters.models.includes(model.name)}
                          onChange={() => toggleModelFilter(model.name)}
                        />
                      ))}
                    </div>
                  </FilterSection>
                )}

                {/* Body Type Filter */}
                <FilterSection 
                  title={t('inventory.vehicleType')} 
                  icon={<Car className="h-4 w-4" />}
                  defaultOpen={true}
                >
                  <div className="space-y-0.5">
                    {bodyTypeStats.map((type) => (
                      <CheckboxItem
                        key={type.name}
                        label={bodyTypeLabels[type.name] || type.name}
                        count={type.count}
                        checked={filters.body_types.includes(type.name)}
                        onChange={() => toggleBodyTypeFilter(type.name)}
                      />
                    ))}
                  </div>
                </FilterSection>

                {/* Price Filter */}
                <FilterSection 
                  title={t('hero.budget')} 
                  icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
                  defaultOpen={false}
                >
                  <div className="space-y-3">
                    <select 
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm bg-gray-50 text-gray-800 focus:border-[#3b82f6] focus:ring-2 focus:ring-blue-100 outline-none transition-all cursor-pointer hover:border-gray-300"
                      value={filters.price_max || ''}
                      onChange={(e) => setFilters(prev => ({ ...prev, price_max: e.target.value ? parseInt(e.target.value) : null }))}
                    >
                      <option value="">{t('inventory.maxPrice')}</option>
                      <option value="15000">{t('inventory.lessThan')} 15 000$</option>
                      <option value="20000">{t('inventory.lessThan')} 20 000$</option>
                      <option value="25000">{t('inventory.lessThan')} 25 000$</option>
                      <option value="30000">{t('inventory.lessThan')} 30 000$</option>
                      <option value="40000">{t('inventory.lessThan')} 40 000$</option>
                      <option value="50000">{t('inventory.lessThan')} 50 000$</option>
                  </select>
                </div>
              </FilterSection>

              {/* KM Filter */}
              <FilterSection 
                title={t('inventory.mileage')} 
                icon={<Gauge className="h-4 w-4" />}
                defaultOpen={false}
              >
                <div className="space-y-3">
                  <select 
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm bg-gray-50 text-gray-800 focus:border-[#3b82f6] focus:ring-2 focus:ring-blue-100 outline-none transition-all cursor-pointer hover:border-gray-300"
                    value={filters.mileage_max || ''}
                    onChange={(e) => setFilters(prev => ({ ...prev, mileage_max: e.target.value ? parseInt(e.target.value) : null }))}
                  >
                    <option value="">{t('inventory.maxMileage')}</option>
                    <option value="50000">{t('inventory.lessThan')} 50 000 km</option>
                    <option value="75000">{t('inventory.lessThan')} 75 000 km</option>
                    <option value="100000">{t('inventory.lessThan')} 100 000 km</option>
                    <option value="125000">{t('inventory.lessThan')} 125 000 km</option>
                    <option value="150000">{t('inventory.lessThan')} 150 000 km</option>
                  </select>
                </div>
              </FilterSection>
              </div>
            </div>
          </aside>

          {/* Main Content Area */}
          <main className="flex-1">
            {/* Mobile Search Box - visible only on smaller screens */}
            <div className="lg:hidden mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder={t('inventory.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-10 py-3 bg-white border border-gray-200 rounded-xl text-sm focus:border-[#3b82f6] focus:ring-2 focus:ring-[#3b82f6]/20 outline-none transition-all shadow-sm"
                  data-testid="inventory-search-input-mobile"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-5 w-5" />
                  </button>
                )}
              </div>
            </div>
            
            {/* Results Header */}
            <div className="flex items-center justify-between mb-4">
              <p className="text-white/90">
                <span className="font-bold text-white">{filteredVehicles.length}</span> {t('inventory.vehiclesFound')}
              </p>
              <div className="flex items-center gap-2">
                <span className="text-sm text-white">{t('inventory.sortBy')}</span>
                <Select value={`${sortBy}-${sortOrder}`} onValueChange={handleSortChange}>
                  <SelectTrigger className="w-[160px] bg-white/90 border-white/20 text-gray-800" data-testid="sort-select">
                    <SelectValue placeholder={t('inventory.recommended')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="created_at-desc">{t('inventory.recommended')}</SelectItem>
                    <SelectItem value="price-asc">{t('inventory.priceLowHigh')}</SelectItem>
                    <SelectItem value="price-desc">{t('inventory.priceHighLow')}</SelectItem>
                    <SelectItem value="year-desc">{t('inventory.yearNewOld')}</SelectItem>
                    <SelectItem value="mileage-asc">{t('inventory.mileageLowHigh')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Vehicle Grid */}
            {apiError && allVehicles.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-xl" data-testid="api-error-message">
                <div className="text-5xl mb-4">
                  <i className="fa-solid fa-triangle-exclamation text-amber-500"></i>
                </div>
                <p className="text-lg text-gray-700 mb-2 font-medium">{t('inventory.connectionError', 'Connexion temporairement interrompue')}</p>
                <p className="text-sm text-gray-500 mb-6">{t('inventory.connectionErrorDetail', 'Nos véhicules seront de retour dans un instant.')}</p>
                <button
                  onClick={() => { fetchAllVehicles(); fetchVehicles(); }}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                  data-testid="retry-button"
                >
                  {t('inventory.retry', 'Réessayer')}
                </button>
              </div>
            ) : loading && allVehicles.length === 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-2 xl:grid-cols-3 gap-3 md:gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="bg-white animate-pulse rounded-xl" style={{ aspectRatio: '16/14' }} />
                ))}
              </div>
            ) : filteredVehicles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 auto-rows-fr" data-testid="vehicle-grid">
                {getGridItems().map((item, index) => (
                  item.type === 'large-promo' ? (
                    <LargePromoCard 
                      key={`promo-${index}`}
                      type={item.data.type}
                      title={item.data.title}
                      subtitle={item.data.subtitle}
                      image={item.data.image}
                      buttonText={item.data.buttonText}
                      buttonLink={item.data.buttonLink}
                    />
                  ) : (
                    <VehicleCard key={item.data.id} vehicle={item.data} priority={index < 4} />
                  )
                ))}
              </div>
            ) : (
              <div className="text-center py-16 bg-white rounded-xl" data-testid="no-vehicles-message">
                <p className="text-lg text-gray-500 mb-4">{t('inventory.noVehicles')}</p>
                <button 
                  onClick={() => {
                    setFilters({ makes: [], body_types: [], price_min: null, price_max: null, mileage_max: null });
                    setSearchQuery('');
                  }}
                  className="text-blue-600 font-medium hover:underline"
                >
                  {t('inventory.clearFilters')}
                </button>
              </div>
            )}
          </main>
        </div>
      </div>

      <div className="relative z-10">
        <SeoCityCluster />
        <Footer />
      </div>
    </div>
  );
};

export default InventoryPage;
